'use strict';

import { isPlainObject, defaults, difference } from 'lodash/fp';

const STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
const FN_ARGS = /^function\s*[^\(]*\(\s*([^\)]*)\)/m;
const FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
const FN_ARG_SPLIT = /,/;

/**
 * Injects calling constructor function parameters as constructed object
 * instance members with the same name.
 *
 * @param {Object} context The context object (this) in which the the calling function is running.
 * @param {Array} arguments Argument values that will be assigned to injected members (usually just provide "arguments" array like object).
 * @param {Object} [options={}] Inject options.
 * @param {String|Array} [options.exclude=null] Comma separated list or array of parameter names to exclude from injection.
 * @param {String} [options.nestUnder=''] Define whether injected parameters should be nested under a specific member (gets replaced if exists).
 * @param {String} [options.stripPrefix=''] Set to true to strip "$" and "_" parameter name prefix when injecting members.
 */
Function.prototype.injectArguments = function injectArguments(context, values, options = {}) {
  // Only execute this function if caller is used as a constructor
  if (!(context instanceof this)) {
    return;
  }

  if (isPlainObject(values)) {
    return Object.keys(values).map(i => {
      context[i] = values[i];
    });
  }

  defaults(options, this.injectArguments.defaults);

  let fn = this;
  let fnText = fn.toString().replace(STRIP_COMMENTS, '');
  let argDecl = fnText.match(FN_ARGS);
  let args = argDecl[1].split(FN_ARG_SPLIT).map(arg => {
    return arg.replace(FN_ARG, (all, underscore, name) => name);
  });

  // Exclude
  if (options.exclude) {
    if (angular.isString(options.exclude)) {
      options.exclude = options.exclude.split(FN_ARG_SPLIT).map(e => e.trim());
    }
    args = difference(args, options.exclude);
  }

  // Nest under
  if (options.nestUnder && !context[options.nestUnder]) {
    context[options.nestUnder] = {};
  }

  if (!args || !values) {
    return;
  }

  // Inject arguments by their name as object instance members
  for (let i = 0, len = args.length; i < len; ++i) {
    let name = args[i];

    // Strip prefix
    if (options.stripPrefix && (name.charAt(0) === '$' || name.charAt(0) === '_')) {
      name = name.substr(1);
    }

    // Nest under
    if (options.nestUnder) {
      context[options.nestUnder][name] = values[i];
    }
    else {
      context[name] = values[i];
    }
  }
};

Function.prototype.injectArguments.defaults = {
  // Comma separated list or array of parameter names to exclude from injection.
  exclude: null,

  // Define whether injected parameters should be nested under a specific member (gets replaced if exists)
  nestUnder: '',

  // Set to true to strip "$" and "_" parameter name prefix when injecting members
  stripPrefix: false
};
